import { create } from "zustand";
import { persist } from "zustand/middleware";
import axios from "axios";

const firstUrl = 'https://ledmap-backend.vercel.app/';
const offlineApiUrl = 'http://localhost:5000';
const onlineApiUrl = 'https://kiana-led-server-iz6c.vercel.app';

const api = axios.create({
    baseURL: firstUrl,
    headers: {
        'Content-Type': 'application/json'
    }
})

const useAuthStore = create(
  persist(
    (set, get) => ({
      token: null,
      user: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      
      // For storing the email during verification process
      verificationEmail: null,
      
      // Step 1: Initiate signup (request verification code)
      initiateSignup: async (userData) => {
        set({ isLoading: true, error: null });
        try {
          const response = await api.post('/api/auth/initiate-signup', userData);
          
          // Store the email for the verification step
          set({ 
            verificationEmail: response.data.email,
            isLoading: false 
          });
          return true;
        } catch (error) {
          set({ 
            error: error.response?.data?.message || "Registration failed", 
            isLoading: false 
          });
          return false;
        }
      },
      
      // Step 2: Verify and complete signup
      verifySignup: async (verificationCode) => {
        const email = get().verificationEmail;
        if (!email) {
          set({ error: "Verification session expired" });
          return false;
        }
        
        set({ isLoading: true, error: null });
        try {
          const response = await api.post('/api/auth/verify-signup', {
            email,
            verificationCode
          });
          
          const { token, user } = response.data;
          
          // Set auth token for all future requests
          api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          
          set({ 
            token, 
            user, 
            isAuthenticated: true, 
            isLoading: false,
            verificationEmail: null // Clear the verification email
          });
          return true;
        } catch (error) {
          set({ 
            error: error.response?.data?.message || "Verification failed", 
            isLoading: false 
          });
          return false;
        }
      },
      
      // Resend verification code
      resendVerificationCode: async () => {
        const email = get().verificationEmail;
        if (!email) {
          set({ error: "Verification session expired" });
          return false;
        }
        
        set({ isLoading: true, error: null });
        try {
          await api.post('/api/auth/resend-verification', { email });
          set({ isLoading: false });
          return true;
        } catch (error) {
          set({ 
            error: error.response?.data?.message || "Failed to resend verification code", 
            isLoading: false 
          });
          return false;
        }
      },
      
      // Clear verification email
      clearVerification: () => set({ verificationEmail: null }),
      
      // For backwards compatibility and any direct signup needs
      register: async (userData) => {
        // First initiate signup
        const initiateSuccess = await get().initiateSignup(userData);
        if (!initiateSuccess) return false;
        
        // Note: This won't actually complete registration,
        // but it preserves the API compatibility while moving 
        // to a verification-based flow
        return true;
      },

      // Login user
      login: async (email, password) => {
        set({ isLoading: true, error: null });
        try {
          const response = await api.post('/api/auth/login', { email, password });
          const { token, user } = response.data;
          
          // Set auth token for all future requests
          api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          
          set({ 
            token, 
            user, 
            isAuthenticated: true, 
            isLoading: false 
          });
          return true;
        } catch (error) {
          set({ 
            error: error.response?.data?.message || "Login failed", 
            isLoading: false 
          });
          return false;
        }
      },

      // Logout user
      logout: () => {
        // Remove auth token
        delete api.defaults.headers.common['Authorization'];
        
        set({ 
          token: null, 
          user: null, 
          isAuthenticated: false,
          verificationEmail: null // Also clear any pending verification
        });
        
        // Instead of redirecting here, let components handle navigation
        return true;
      },

      // Clear error
      clearError: () => set({ error: null }),

      // Add these new functions for password reset flow
      requestPasswordReset: async (email) => {
        set({ isLoading: true, error: null });
        try {
          const response = await api.post('/api/auth/forgot-password', { email });
          set({ 
            resetEmail: email,
            isLoading: false 
          });
          return true;
        } catch (error) {
          set({ 
            error: error.response?.data?.message || "Failed to request password reset", 
            isLoading: false 
          });
          return false;
        }
      },
    
      verifyResetCode: async (resetCode) => {
        const email = get().resetEmail;
        if (!email) {
          set({ error: "Reset session expired" });
          return false;
        }
        
        set({ isLoading: true, error: null });
        try {
          const response = await api.post('/api/auth/verify-reset-code', {
            email,
            resetCode
          });
          
          set({ 
            resetToken: response.data.resetToken,
            isLoading: false 
          });
          return true;
        } catch (error) {
          set({ 
            error: error.response?.data?.message || "Verification failed", 
            isLoading: false 
          });
          return false;
        }
      },

      resetPassword: async (newPassword) => {
        const resetToken = get().resetToken;
        if (!resetToken) {
          set({ error: "Reset session expired" });
          return false;
        }
        
        set({ isLoading: true, error: null });
        try {
          await api.post('/api/auth/reset-password', {
            resetToken,
            newPassword
          });
          
          // Clear reset data
          set({ 
            resetEmail: null,
            resetToken: null,
            isLoading: false 
          });
          return true;
        } catch (error) {
          set({ 
            error: error.response?.data?.message || "Failed to reset password", 
            isLoading: false 
          });
          return false;
        }
      },

      clearResetData: () => {
        set({ resetEmail: null, resetToken: null });
      },
      
      // Add change password functionality for logged-in users
      changePassword: async (currentPassword, newPassword) => {
        set({ isLoading: true, error: null });
        try {
          const response = await api.post('/api/auth/change-password', {
            currentPassword,
            newPassword
          });
          
          set({ 
            isLoading: false,
            lastSuccessMessage: response.data.message
          });
          return { success: true, message: response.data.message };
        } catch (error) {
          const errorMessage = error.response?.data?.message || "Failed to change password";
          set({ 
            error: errorMessage, 
            isLoading: false 
          });
          return { success: false, message: errorMessage };
        }
      }
    }),
    {
      name: "auth-storage", // name of item in localStorage
      getStorage: () => localStorage, // (optional) by default, 'localStorage' is used
    }
  )
);

export default useAuthStore;
